import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSliderModule } from '@angular/material/slider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatExpansionModule } from '@angular/material/expansion';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { InventoryQueryParams } from '../../../../utils/interfaces/inventory-item.interface';
import { InventoryService } from '../../../../services/inventory/inventory.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import {
  INITIAL_FILTER_VALUES,
  INVENTORY_SORT_DIRECTIONS,
  INVENTORY_SORT_OPTIONS,
} from '../../../../utils/constants/inventory-table.constant';
import {
  ActiveInventoryCriterion,
  FilterSection,
  RANGE_FILTER_CONFIGS,
  FilterConfig,
} from '../../../../utils/types/filter.types';
import {
  FilterOption,
  InventoryFilterOptions,
} from '../../../../utils/interfaces/filter-options.interface';
import {
  buildFilterParams,
  isValidSearchTerm,
} from '../../../../utils/helpers/inventory-filter.helpers';

@Component({
  selector: 'app-inventory-filter-drawer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSidenavModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatSliderModule,
    TranslatePipe,
    MatExpansionModule,
  ],
  templateUrl: './inventory-filter-drawer.component.html',
  styleUrl: './inventory-filter-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InventoryFilterDrawerComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly inventoryService = inject(InventoryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  public readonly filterApplied = output<InventoryQueryParams>();
  public readonly filterReset = output();

  public isOpen = signal(false);
  public filterForm!: FormGroup;

  public categories = signal<InventoryFilterOptions['categories']>([]);
  public statuses = signal<InventoryFilterOptions['statuses']>([]);
  public readonly sortOptions = INVENTORY_SORT_OPTIONS;
  public readonly sortDirections = INVENTORY_SORT_DIRECTIONS;

  public readonly activeCriteria = signal<ActiveInventoryCriterion[]>([]);
  public readonly hasActiveCriteria = computed(() => this.activeCriteria().length > 0);

  public isObject(value: unknown): value is FilterOption {
    return (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      'id' in value &&
      'name' in value
    );
  }

  public filtersConfig: FilterConfig[] = [
    {
      key: 'productName',
      type: 'text',
      title: 'inventory.filter.productName',
      placeholder: 'inventory.filter.findInList',
    },
    {
      key: 'productLocation',
      type: 'text',
      title: 'inventory.filter.productLocation',
      placeholder: 'inventory.filter.findInList',
    },
    {
      key: 'brandName',
      type: 'text',
      title: 'inventory.filter.brand',
      placeholder: 'inventory.filter.findInList',
    },
    { key: 'price', type: 'range', title: 'inventory.filter.price', min: 0, max: 1000, step: 10 },
    {
      key: 'availableAmount',
      type: 'range',
      title: 'inventory.filter.availableAmount',
      min: 0,
      max: 1000,
      step: 10,
    },
    {
      key: 'reservedAmount',
      type: 'range',
      title: 'inventory.filter.reservedAmount',
      min: 0,
      max: 1000,
      step: 10,
    },
    {
      key: 'onHandAmount',
      type: 'range',
      title: 'inventory.filter.onHandAmount',
      min: 0,
      max: 1000,
      step: 10,
    },
    {
      key: 'category',
      type: 'select',
      title: 'inventory.filter.category',
      options: () => this.categories(),
      placeholder: 'inventory.messages.chooseCategory',
      emptyPlaceholder: 'inventory.messages.noCategories',
    },
    {
      key: 'status',
      type: 'select',
      title: 'inventory.filter.status',
      options: () => this.statuses(),
      placeholder: 'inventory.messages.chooseStatus',
      emptyPlaceholder: 'inventory.messages.noStatuses',
    },
  ];

  public ngOnInit(): void {
    this.initializeForm();
    this.setupFormValueChanges();
  }

  public open(): void {
    this.isOpen.set(true);

    if (this.categories().length === 0) {
      this.loadFilterOptions();
    }
  }

  public close(): void {
    this.isOpen.set(false);
  }

  public applyFilters(): void {
    const filters = buildFilterParams(this.filterForm.value);
    const criteria = this.buildActiveCriteria(this.filterForm.value);

    this.activeCriteria.set(criteria);
    this.filterApplied.emit(filters);
  }

  public resetFilters(): void {
    this.filterForm.reset(INITIAL_FILTER_VALUES);
    this.activeCriteria.set([]);
    this.filterReset.emit();
  }

  public resetSection(section: FilterSection): void {
    if (section in RANGE_FILTER_CONFIGS) {
      const config = RANGE_FILTER_CONFIGS[section as keyof typeof RANGE_FILTER_CONFIGS];
      this.filterForm.patchValue({
        [config.minControl]: config.defaultMin,
        [config.maxControl]: config.defaultMax,
      });
    } else if (section === 'sortBy') {
      this.filterForm.patchValue({ sortBy: '', sortOrder: '' });
    } else {
      const fieldMap: Record<string, string> = {
        productName: 'productName',
        brandName: 'brandName',
        category: 'category',
        productLocation: 'productLocation',
        status: 'status',
      };

      this.filterForm.patchValue({ [fieldMap[section]]: '' });
    }

    this.applyFilters();
  }

  public onRangeSliderChange(
    value: number,
    controlName: string,
    pairControlName: string,
    type: 'min' | 'max',
  ): void {
    const control = this.getControl(controlName);
    const pairControl = this.getControl(pairControlName);

    control.setValue(value, { emitEvent: false });

    if (type === 'min' && value > pairControl.value) {
      pairControl.setValue(value, { emitEvent: false });
    }

    if (type === 'max' && value < pairControl.value) {
      pairControl.setValue(value, { emitEvent: false });
    }

    this.applyFilters();
  }

  public onRangeInputChange(minName: string, maxName: string): void {
    const min = this.getControl(minName);
    const max = this.getControl(maxName);

    if (min.value > max.value) {
      max.setValue(min.value, { emitEvent: false });
    }

    this.applyFilters();
  }

  public onSortFieldChange(): void {
    if (!this.getControl('sortOrder').value) {
      this.getControl('sortOrder').setValue('asc', { emitEvent: false });
    }

    this.applyFilters();
  }

  private initializeForm(): void {
    this.filterForm = this.fb.group({
      ...INITIAL_FILTER_VALUES,
      productName: [''],
      brandName: [''],
      productLocation: [''],
    });
  }

  private setupFormValueChanges(): void {
    const textFields = ['productName', 'brandName', 'productLocation'];

    textFields.forEach((fieldName) => {
      this.setupTextFieldDebounce(fieldName);
    });
  }

  private setupTextFieldDebounce(fieldName: string): void {
    this.filterForm
      .get(fieldName)
      ?.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter((value) => isValidSearchTerm(value || '')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.applyFilters();
      });
  }

  private getControl(name: string): FormControl {
    return this.filterForm.get(name) as FormControl;
  }

  private loadFilterOptions(): void {
    this.inventoryService
      .getFilterOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options: InventoryFilterOptions) => {
        this.categories.set(options.categories);
        this.statuses.set(options.statuses);
      });
  }

  private buildActiveCriteria(formValue: Record<string, unknown>): ActiveInventoryCriterion[] {
    const criteria: ActiveInventoryCriterion[] = [];

    this.addTextCriterion(criteria, 'productName', 'inventory.filter.productName', formValue);
    this.addTextCriterion(
      criteria,
      'productLocation',
      'inventory.filter.productLocation',
      formValue,
    );
    this.addTextCriterion(criteria, 'brandName', 'inventory.filter.brand', formValue);

    if (formValue['category']) {
      criteria.push({
        key: 'category',
        label: 'inventory.filter.category',
        value: this.getCategoryLabel(Number(formValue['category'])),
      });
    }

    if (formValue['status']) {
      criteria.push({
        key: 'status',
        label: 'inventory.filter.status',
        value: String(formValue['status']),
      });
    }

    this.addRangeCriterion(criteria, 'price', 'inventory.filter.price', formValue);
    this.addRangeCriterion(
      criteria,
      'availableAmount',
      'inventory.filter.availableAmount',
      formValue,
    );
    this.addRangeCriterion(
      criteria,
      'reservedAmount',
      'inventory.filter.reservedAmount',
      formValue,
    );
    this.addRangeCriterion(criteria, 'onHandAmount', 'inventory.filter.onHandAmount', formValue);

    if (formValue['sortBy'] && formValue['sortOrder']) {
      const sortOption = this.sortOptions.find((option) => option.value === formValue['sortBy']);
      const sortDirection = this.sortDirections.find(
        (option) => option.value === formValue['sortOrder'],
      );

      criteria.push({
        key: 'sortBy',
        label: 'inventory.filter.sortBy',
        value: [
          this.translate.instant(sortOption?.label ?? String(formValue['sortBy'])),
          this.translate.instant(sortDirection?.label ?? String(formValue['sortOrder'])),
        ].join(' '),
      });
    }

    return criteria;
  }

  private addTextCriterion(
    criteria: ActiveInventoryCriterion[],
    key: Extract<FilterSection, 'productName' | 'productLocation' | 'brandName'>,
    label: string,
    formValue: Record<string, unknown>,
  ): void {
    const value = String(formValue[key] ?? '').trim();

    if (value) {
      criteria.push({ key, label, value });
    }
  }

  private addRangeCriterion(
    criteria: ActiveInventoryCriterion[],
    key: Extract<FilterSection, 'price' | 'availableAmount' | 'reservedAmount' | 'onHandAmount'>,
    label: string,
    formValue: Record<string, unknown>,
  ): void {
    const config = RANGE_FILTER_CONFIGS[key];
    const min = Number(formValue[config.minControl] ?? config.defaultMin);
    const max = Number(formValue[config.maxControl] ?? config.defaultMax);

    if (min !== config.defaultMin || max !== config.defaultMax) {
      criteria.push({ key, label, value: `${min} - ${max}` });
    }
  }

  private getCategoryLabel(categoryId: number): string {
    return (
      this.categories().find((category) => category.id === categoryId)?.name ?? String(categoryId)
    );
  }
}
